import { CONNECT_SETTLE_MS } from '@playcastdotio/PeerCore';

/**
 * How long past the settle window the gate keeps waiting for a trusted calibrated floor
 * before it stops requiring one. A guest whose engine cannot calibrate glass-to-glass at all
 * (no rVFC timestamps) would otherwise never settle, and its congestion response would be
 * disabled for the whole session — a worse failure than acting on an uncalibrated floor.
 */
export const CONNECT_SETTLE_FLOOR_WAIT_FACTOR = 3;

export type ConnectSettleGateOptions = {
  settleMs?: number;
  /** Ceiling on the wait for a trusted floor, from first stats. Default `settleMs × 3`. */
  maxFloorWaitMs?: number;
  nowMs?: () => number;
};

/**
 * ConnectSettleGate — startup is not congestion.
 *
 * Field data (Android, 2026-09-19): the controller cut the bitrate cap 77 % and raised a
 * sticky soft floor on the first or second guest sample after connect, while glass-to-glass
 * was at its best of the run (19 ms). The first seconds of a stream carry a startup burst, a
 * cold jitter buffer and an untrusted playout baseline; none of it is evidence.
 *
 * Per peer, no cut, floor raise, fps-ceiling lowering or tier change may be applied until
 * CONNECT_SETTLE_MS have passed since the peer's first `clientStats` AND the calibrated
 * latency floor is trusted. A peer that has never sent `clientStats` is not gated: it has no
 * startup sample to be fooled by, and its host-side triggers carry their own warm-up.
 *
 * The one exception is explicit and passed by the caller: a `loss` or `freeze` critical is
 * an instantaneous, network-proven signal and bypasses the gate (`bypass: true`).
 */
export class ConnectSettleGate {
  private readonly settleMs: number;
  private readonly maxFloorWaitMs: number;
  private readonly nowMs: () => number;
  private readonly firstStatsAtByPeer = new Map<string, number>();

  constructor(opts: ConnectSettleGateOptions = {}) {
    this.settleMs = opts.settleMs ?? CONNECT_SETTLE_MS;
    this.maxFloorWaitMs = opts.maxFloorWaitMs ?? this.settleMs * CONNECT_SETTLE_FLOOR_WAIT_FACTOR;
    this.nowMs = opts.nowMs ?? (() => Date.now());
  }

  /** Arm the gate at the peer's first `clientStats`. Later calls are ignored. */
  public noteFirstStats(peerId: string): void {
    if (this.firstStatsAtByPeer.has(peerId)) return;
    this.firstStatsAtByPeer.set(peerId, this.nowMs());
  }

  /** True once the gate has been armed for this peer. */
  public isArmed(peerId: string): boolean {
    return this.firstStatsAtByPeer.has(peerId);
  }

  /**
   * True when cuts, floors, fps ceilings and tier changes may be applied for this peer.
   *
   * @param calibratedFloorTrusted whether the peer's calibrated latency floor is trusted.
   * @param bypass true for a loss- or freeze-critical verdict, which never waits.
   */
  public settled(peerId: string, calibratedFloorTrusted: boolean, bypass = false): boolean {
    if (bypass) return true;
    const firstStatsAt = this.firstStatsAtByPeer.get(peerId);
    if (firstStatsAt === undefined) return true;
    const elapsed = this.nowMs() - firstStatsAt;
    if (elapsed < this.settleMs) return false;
    if (!calibratedFloorTrusted && elapsed < this.maxFloorWaitMs) return false;
    return true;
  }

  /** Milliseconds since the peer's first stats, or null when never armed — for debug lines. */
  public sinceFirstStatsMs(peerId: string): number | null {
    const firstStatsAt = this.firstStatsAtByPeer.get(peerId);
    return firstStatsAt === undefined ? null : this.nowMs() - firstStatsAt;
  }

  public clearPeer(peerId: string): void {
    this.firstStatsAtByPeer.delete(peerId);
  }

  public clearAll(): void {
    this.firstStatsAtByPeer.clear();
  }
}
